import { useCallback, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import type { OpenAPI } from "simplydo/interfaces";
import { RichText } from "simplydo/react/richtext";
import { Button } from "simplydo/react/ui/button";
import { Form, FormField, FormInput } from "simplydo/react/ui/form";
import { H4 } from "simplydo/react/ui/header";
import { Input } from "simplydo/react/ui/input";
import { Menu, MenuItem } from "simplydo/react/ui/menu";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { Tab, TabPane } from "simplydo/react/ui/tab";
import { Table, TableCell, TableRow } from "simplydo/react/ui/table";

import api from "api";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import { LargeCheckbox } from "components/lib/UI";

import type { IdeaWithAuthors } from "../../types";

type IdeaEmailModalProps = {
  idea: IdeaWithAuthors;
  isOpen: boolean;
  setOpen: (open: boolean) => void;
};

type TablePaneProps = {
  users: OpenAPI.Schemas["User"][] | undefined;
  selectedUserIds: string[];
  setSelectedUserIds: React.Dispatch<React.SetStateAction<string[]>>;
};

const TablePane = ({
  users,
  selectedUserIds,
  setSelectedUserIds,
}: TablePaneProps) => {
  const [filter, setFilter] = useState("");
  const filteredUsers = useMemo(() => {
    if (!users?.length) {
      return [];
    }
    if (!filter) {
      return users;
    }

    return users.filter((user: OpenAPI.Schemas["User"]) =>
      user.profile?.fullName?.toLowerCase().includes(filter),
    );
  }, [users, filter]);

  return (
    <TabPane>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          gap: 12,
          alignItems: "center",
        }}
      >
        <Input
          onChange={(e, { value }) => setFilter(value.toLowerCase())}
          placeholder="Filter users"
        />
        <Button
          onClick={() => setSelectedUserIds((users ?? []).map((u) => u._id!))}
        >
          Select all
        </Button>
        <span style={{ color: "#888" }}>
          Showing {filteredUsers.length} / {users?.length ?? 0}
        </span>
      </div>

      {filteredUsers.length ? (
        <Table size="small">
          {filteredUsers.map((user: OpenAPI.Schemas["User"]) => (
            <TableRow key={user._id}>
              <TableCell collapsing>
                <LargeCheckbox
                  checked={selectedUserIds.includes(user._id!)}
                  onChange={() => {
                    setSelectedUserIds((prev: string[]) =>
                      prev.includes(user._id!)
                        ? prev.filter((selectedId) => selectedId !== user._id)
                        : [...prev, user._id!],
                    );
                  }}
                />
              </TableCell>
              <TableCell>
                <UserChip user={user} compact />
              </TableCell>
            </TableRow>
          ))}
        </Table>
      ) : null}
    </TabPane>
  );
};

type GetPaneProps = {
  title: string;
  users: OpenAPI.Schemas["User"][] | undefined;
  selectedUserIds: string[];
  setSelectedUserIds: React.Dispatch<React.SetStateAction<string[]>>;
};

const getPane = ({
  title,
  users,
  selectedUserIds,
  setSelectedUserIds,
}: GetPaneProps) => {
  if (!users) {
    return {};
  }
  if (users.length === 0) {
    return {};
  }

  return {
    menuItem: <MenuItem>{title}</MenuItem>,
    render: () => (
      <TablePane
        users={users}
        selectedUserIds={selectedUserIds}
        setSelectedUserIds={setSelectedUserIds}
      />
    ),
  };
};

const IdeaEmailModal = ({ idea, isOpen, setOpen }: IdeaEmailModalProps) => {
  const { t } = useTranslation();
  const [selectedUserIds, setSelectedUserIds] = useState<Array<string>>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState("");

  const board = idea.projectManagement?.boards?.find(
    (board) => board.forId === idea.challenge,
  ) as
    | (NonNullable<IdeaWithAuthors["projectManagement"]>["boards"][number] & {
        assigneeUsers?: OpenAPI.Schemas["User"][];
      })
    | undefined;
  const uniqueAssessmentUsers = useMemo(() => {
    return (
      idea.assessments
        ?.flatMap((assessment) => {
          const ownerUser = (
            assessment as { ownerUser?: OpenAPI.Schemas["User"] }
          ).ownerUser;
          return ownerUser ? [ownerUser] : [];
        })
        .filter(Boolean)
        .filter(
          (user, index, self) =>
            index === self.findIndex((t) => t?._id === user?._id),
        ) || []
    );
  }, [idea.assessments]);

  const sendEmail = useCallback(() => {
    util
      .confirm(
        "Send email",
        `Are you sure you want to send this email to ${util.pluralise(selectedUserIds.length, "user", "users")}?`,
      )
      .then(() => {
        api.ideas.sendEmail(
          idea._id,
          { users: selectedUserIds, subject, body, replyTo },
          () => {
            toast.success("Email was sent to users");
            // Briefly delay invalidation to await insertion of new log
            setTimeout(() => {
              api.invalidateQueries(api.ideas.getEmailLogs, [idea?._id]);
            }, 500);
            setOpen(false);
            setSubject("");
            setBody("");
            setReplyTo("");
            setSelectedUserIds([]);
          },
          (e) => {
            toast.error(e.message);
          },
        );
      })
      .catch(() => {});
  }, [idea._id, selectedUserIds, subject, body, replyTo, setOpen]);

  return (
    <Modal open={isOpen} size="small">
      <ModalHeader>Send email to {t("generic.idea")} stakeholders</ModalHeader>
      <ModalContent scrolling>
        <H4>1. Choose recipients</H4>
        <Tab
          panes={[
            getPane({
              title: "Authors",
              users: idea.authors,
              selectedUserIds,
              setSelectedUserIds,
            }),
            getPane({
              title: "Assignees",
              users: board?.assigneeUsers,
              selectedUserIds,
              setSelectedUserIds,
            }),
            getPane({
              title: "Assessors",
              users: uniqueAssessmentUsers,
              selectedUserIds,
              setSelectedUserIds,
            }),
          ]}
        />

        <H4>2. Compose message</H4>
        <p>
          Write a message that is relevant to your selected recipients. If you
          would like the users to be able to reply to this email directly please
          also attach a Reply-To address.
        </p>
        <Form style={{ marginTop: 16 }}>
          <FormInput
            label="Subject"
            onChange={(e, { value }) => {
              setSubject(value);
            }}
          />
          <FormField
            control={RichText}
            label="Email body"
            forType="idea"
            hideMenu={false}
            forId={idea._id}
            preset="simple"
            onChange={(html: string) => {
              setBody(html);
            }}
            value={body}
          />
          <FormInput
            label="Reply-To Address"
            placeholder="(optional)"
            onChange={(e, { value }) => {
              setReplyTo(value);
            }}
          />
        </Form>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => setOpen(false)}>Close</Button>
        <Button
          onClick={sendEmail}
          disabled={!selectedUserIds.length || !subject || !body}
          color="primary"
        >
          Send email
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default IdeaEmailModal;
